// frontend/src/pages/LoginPage.js - Updated to match mobile experience
import React, { useState } from 'react';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const LoginFlow = ({ onLogin, onSignUp, onBack }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Forgot password flow state: null | 'email' | 'code' | 'done'
  const [resetStep, setResetStep] = useState(null);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetInfo, setResetInfo] = useState('');

  const handleLogin = async () => {
    if (!email || !password) {
      setErrorMessage("Please fill in all fields");
      setShowError(true);
      return;
    }

    setIsLoading(true);
    
    const result = await onLogin({ email, password });
    
    if (!result.success) {
      setIsLoading(false);
      setErrorMessage(result.message || "Sorry we couldn't find your details. Please double-check or Reset Password");
      setShowError(true);
    }
  };

  const handleForgotPassword = () => {
    setResetEmail(email); // pre-fill with whatever they typed
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setResetError('');
    setResetInfo('');
    setResetStep('email');
  };

  const resetRequest = async (path, body) => {
    const response = await fetch(`${API_BASE_URL}/passwordreset/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
      throw new Error(data.message || 'Request failed. Please try again.');
    }
    return data;
  };

  const handleSendResetCode = async () => {
    if (!resetEmail) {
      setResetError('Please enter your email address');
      return;
    }
    setResetLoading(true);
    setResetError('');
    try {
      await resetRequest('initiate', { email: resetEmail });
      setResetInfo('If this email exists, a 6-digit code has been sent. It expires in 15 minutes.');
      setResetStep('code');
    } catch (err) {
      setResetError(err.message);
    } finally {
      setResetLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResetLoading(true);
    setResetError('');
    try {
      await resetRequest('resend', { email: resetEmail });
      setResetInfo('A new code has been sent.');
    } catch (err) {
      setResetError(err.message);
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetCode || !newPassword || !confirmPassword) {
      setResetError('Please fill in all fields');
      return;
    }
    if (newPassword.length < 8) {
      setResetError('Password must be at least 8 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match');
      return;
    }
    setResetLoading(true);
    setResetError('');
    try {
      await resetRequest('reset', {
        email: resetEmail,
        token: resetCode,
        newPassword,
        confirmPassword
      });
      setResetStep('done');
    } catch (err) {
      setResetError(err.message);
    } finally {
      setResetLoading(false);
    }
  };

  const closeResetFlow = () => {
    setResetStep(null);
    setResetError('');
    setResetInfo('');
  };

  const handleRegister = () => {
    if (onSignUp) onSignUp();
  };

  const closeError = () => {
    setShowError(false);
  };

  const retryLogin = () => {
    setShowError(false);
    setEmail('');
    setPassword('');
  };

  // No separate "Logging you in..." screen: the branded loading splash (started
  // on tap by App.handleLogin) covers the auth call + board load as one animation.

  // Forgot password flow
  if (resetStep) {
    const inputStyle = {
      width: '100%',
      padding: '16px',
      backgroundColor: '#374151',
      border: '1px solid #4b5563',
      borderRadius: '8px',
      color: 'white',
      fontSize: '16px',
      outline: 'none',
      boxSizing: 'border-box'
    };
    const labelStyle = {
      display: 'block',
      color: '#e5e7eb',
      fontSize: '14px',
      marginBottom: '8px'
    };
    const primaryButtonStyle = {
      width: '100%',
      padding: '16px',
      background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: resetLoading ? 'not-allowed' : 'pointer',
      opacity: resetLoading ? 0.7 : 1
    };

    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#000',
        color: 'white',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '20px 24px',
          borderBottom: '1px solid #374151'
        }}>
          <button
            onClick={closeResetFlow}
            style={{
              background: 'none',
              border: 'none',
              color: '#3b82f6',
              cursor: 'pointer',
              marginRight: '16px',
              padding: '4px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ArrowLeft size={24} />
          </button>
          <span style={{ fontSize: '20px', fontWeight: 'bold' }}>Reset Password</span>
        </div>

        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '40px 32px 32px',
          maxWidth: '480px',
          margin: '0 auto',
          width: '100%'
        }}>
          {resetStep === 'email' && (
            <>
              <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '24px' }}>
                Enter the email linked to your account and we'll send you a 6-digit verification code.
              </p>
              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>Email</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="Enter your email"
                  style={inputStyle}
                />
              </div>
              {resetError && (
                <p style={{ color: '#ef4444', fontSize: '14px', marginBottom: '16px' }}>{resetError}</p>
              )}
              <button onClick={handleSendResetCode} disabled={resetLoading} style={primaryButtonStyle}>
                {resetLoading ? 'SENDING...' : 'SEND CODE'}
              </button>
            </>
          )}

          {resetStep === 'code' && (
            <>
              {resetInfo && (
                <p style={{ color: '#10b981', fontSize: '14px', marginBottom: '24px' }}>{resetInfo}</p>
              )}
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Verification code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="6-digit code"
                  style={{ ...inputStyle, letterSpacing: '8px', textAlign: 'center' }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>New password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>Confirm new password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  style={inputStyle}
                />
              </div>
              {resetError && (
                <p style={{ color: '#ef4444', fontSize: '14px', marginBottom: '16px' }}>{resetError}</p>
              )}
              <button onClick={handleResetPassword} disabled={resetLoading} style={primaryButtonStyle}>
                {resetLoading ? 'RESETTING...' : 'RESET PASSWORD'}
              </button>
              <button
                onClick={handleResendCode}
                disabled={resetLoading}
                style={{
                  marginTop: '16px',
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  cursor: 'pointer',
                  fontSize: '14px',
                  textDecoration: 'underline'
                }}
              >
                Resend code
              </button>
            </>
          )}

          {resetStep === 'done' && (
            <>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                <h2 style={{ fontSize: '22px', fontWeight: 'bold', marginBottom: '8px' }}>
                  Password reset
                </h2>
                <p style={{ color: '#9ca3af', fontSize: '14px' }}>
                  Your password has been updated. Log in with your new password.
                </p>
              </div>
              <button onClick={closeResetFlow} style={{ ...primaryButtonStyle, cursor: 'pointer', opacity: 1 }}>
                BACK TO LOGIN
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#000',
      color: 'white',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header - matches mobile */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '20px 24px',
        borderBottom: '1px solid #374151'
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: '#3b82f6',
            cursor: 'pointer',
            marginRight: '16px',
            padding: '4px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <ArrowLeft size={24} />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img src="/pb-logo.png" alt="PlayerBet" style={{ width: '36px', height: '36px' }} />
          <span style={{ fontSize: '20px', fontWeight: 'bold' }}>PlayerBet</span>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '40px 32px 32px',
        maxWidth: '480px',
        margin: '0 auto',
        width: '100%'
      }}>
        {/* Avatar Section - matches mobile */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '40px'
        }}>
          <div style={{
            width: '120px',
            height: '120px',
            background: 'linear-gradient(135deg, #374151, #4b5563)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '4px solid #4b5563'
          }}>
            <span style={{
              fontSize: '48px',
              fontWeight: 'bold',
              color: '#9ca3af'
            }}>
              A
            </span>
          </div>
        </div>

        {/* Title and Register Link - matches mobile */}
        <div style={{
          textAlign: 'center',
          marginBottom: '32px'
        }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 'bold',
            marginBottom: '8px'
          }}>
            Log In
          </h1>
          <p style={{
            color: '#9ca3af',
            fontSize: '14px'
          }}>
            Don't have an account?{' '}
            <button
              onClick={handleRegister}
              style={{
                color: '#3b82f6',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '14px'
              }}
            >
              Register
            </button>
          </p>
        </div>

        {/* Form - matches mobile */}
        <div style={{ marginBottom: '32px' }}>
          {/* Email Field */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: '#e5e7eb',
              fontSize: '14px',
              marginBottom: '8px'
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: '#374151',
                border: '1px solid #4b5563',
                borderRadius: '8px',
                color: 'white',
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#4b5563'}
            />
          </div>

          {/* Password Field */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: '#e5e7eb',
              fontSize: '14px',
              marginBottom: '8px'
            }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                style={{
                  width: '100%',
                  padding: '16px',
                  paddingRight: '50px',
                  backgroundColor: '#374151',
                  border: '1px solid #4b5563',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '16px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#4b5563'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Forgot Password */}
          <div style={{ textAlign: 'left', marginBottom: '32px' }}>
            <button
              onClick={handleForgotPassword}
              style={{
                color: '#3b82f6',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                textDecoration: 'underline'
              }}
            >
              Forgot password? Reset
            </button>
          </div>
        </div>

        {/* Login Button */}
        <button
          onClick={handleLogin}
          disabled={isLoading}
          style={{
            width: '100%',
            padding: '16px',
            background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.7 : 1,
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => !isLoading && (e.target.style.transform = 'translateY(-2px)')}
          onMouseOut={(e) => !isLoading && (e.target.style.transform = 'translateY(0)')}
        >
          LOGIN
        </button>
      </div>

      {/* Error Modal - matches mobile */}
      {showError && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '32px',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1f2937',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '320px',
            width: '100%',
            textAlign: 'center',
            border: '1px solid #374151'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '12px',
              color: 'white'
            }}>
              Incorrect log in details
            </h3>
            <p style={{
              color: '#9ca3af',
              fontSize: '14px',
              lineHeight: '1.5',
              marginBottom: '24px'
            }}>
              {errorMessage}
            </p>
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={retryLogin}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#374151',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Re-try
              </button>
              <button
                onClick={closeError}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                LOGIN
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginFlow;